import {
  BalanceOfAbi,
  NativeWithdrawQueueReadAbi,
  NativeWnlpReadAbi,
  TotalSupplyAbi,
} from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Native Credit Pool (wNLP) reader — six calls per entry:
 *   1. `wNLP.totalSupply()`
 *   2. `wNLP.getNlpByWnlp(1e18)`          → 1e18-scaled exchange rate
 *   3. `wNLP.instantRedeemFeeBips()`
 *   4. `wNLP.instantRedeemEnabled()`
 *   5. `underlying.balanceOf(creditVault)` → instant-redeem inventory
 *   6. `withdrawQueue.withdrawalWindow()`  → queue waiting period
 *
 * ## Why not the generic 4626 reader
 *
 * wNLP is not ERC-4626 — `asset()`, `totalAssets()` and
 * `convertToAssets()` revert. `getNlpByWnlp` already returns a
 * **1e18-scaled ratio** regardless of the token's decimals (verified
 * against 6-, 8- and 18-decimal wNLPs), so unlike the 4626 reader there
 * is no `10**underlyingDecimals` rescale — the value is the exchange
 * rate as-is.
 *
 * ## totalAssets
 *
 * Derived as `totalSupply * rate / 1e18` — exactly how the contract
 * itself converts, and identical to `NTLP.balanceOf(wNLP)`, without
 * spending a call on resolving the NTLP address first.
 *
 * ## Liquidity
 *
 * Instant redemption is capped by the chain's single **CreditVault**
 * inventory, not by the pool's size (Native targets a 5–10 % reserve).
 * Reading the CreditVault's underlying balance gives the real
 * this-block exit capacity, which `fetchPublic` clamps to `totalAssets`
 * and reports as `liquidity` — a genuine number rather than the blanket
 * `0` every other non-instant mode uses.
 */
export const readerNativeWnlp = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const { address, underlying, inventoryContract, withdrawQueue } = entry
  return {
    calls: [
      { address, name: 'totalSupply', params: [] },
      { address, name: 'getNlpByWnlp', params: [ONE_E18] },
      { address, name: 'instantRedeemFeeBips', params: [] },
      { address, name: 'instantRedeemEnabled', params: [] },
      // Falls back to the vault itself when no CreditVault is pinned —
      // yields a 0 capacity rather than a malformed call.
      {
        address: underlying,
        name: 'balanceOf',
        params: [inventoryContract ?? address],
      },
      {
        address: withdrawQueue ?? address,
        name: 'withdrawalWindow',
        params: [],
      },
    ],
    abis: [
      TotalSupplyAbi,
      NativeWnlpReadAbi,
      NativeWnlpReadAbi,
      NativeWnlpReadAbi,
      BalanceOfAbi,
      NativeWithdrawQueueReadAbi,
    ],
    parse: ([supply, rate, feeBips, instantEnabled, inventory, window]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      // Only supply + rate are load-bearing; the redemption getters are
      // enrichment and must not drop an otherwise-healthy entry.
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }

      const capacity = toBigInt(inventory)
      const windowSeconds = toBigInt(window)
      const bips = toBigInt(feeBips)

      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
        // `instantRedeemFeeBips` on-chain is already basis points, so it
        // maps 1:1 onto the cross-provider `withdrawFeeBps`.
        withdrawFeeBps: bips !== undefined ? Number(bips) : undefined,
        // `instantRedeemEnabled` is a bool; a reverted call reads as
        // undefined, which we treat as "unknown" rather than "disabled".
        instantRedeemEnabled:
          typeof instantEnabled === 'boolean' ? instantEnabled : undefined,
        instantRedeemCapacity: capacity,
        withdrawalCooldownSeconds:
          windowSeconds !== undefined ? Number(windowSeconds) : undefined,
        withdrawQueue: withdrawQueue?.toLowerCase(),
      }
    },
  }
}
